import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Autocomplete,
  Chip,
  Switch,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { useSelector } from 'react-redux';
import CustomAxios from '../../utils/CustomAxios';
import {
  GrowthModal,
  GrowthPrimaryButton,
  GrowthSecondaryButton,
  GrowthPageLoader,
  GrowthFormGrid,
  GrowthFormSection,
  GrowthFormSwitch,
} from '../growth_hub/GrowthModal';

const emptyForm = {
  product: null,
  title: '',
  points_required: '',
  use_within_days: '7',
  total_quantity: '',
  per_user_limit: '1',
  eligibility: 'default',
  inventory_ids: [],
  valid_from: '',
  valid_until: '',
  is_active: true,
};

const ELIGIBILITY_OPTIONS = [
  { value: 'default', label: 'All customers' },
  { value: 'subscribers', label: 'Active subscribers' },
  { value: 'non_subscribers', label: 'Non-subscribers' },
  { value: 'expired_subscribers', label: 'Expired subscribers' },
];

const STATUS_CHIPS = {
  active: { label: 'Active', bg: '#E3F4EA', fg: '#1E6B3A' },
  scheduled: { label: 'Scheduled', bg: '#E3EEFA', fg: '#0B4F8A' },
  out_of_stock: { label: 'Out of stock', bg: '#FDEBDD', fg: '#9A3412' },
  expired: { label: 'Expired', bg: '#F1EDF2', fg: '#4A4550' },
  inactive: { label: 'Off', bg: '#F1EDF2', fg: '#4A4550' },
};

const CLAIM_STATUS = {
  claimed: 'Claimed, not ordered',
  redeemed: 'Ordered',
  returned: 'Returned (order cancelled)',
  lapsed: 'Lapsed (not ordered in time)',
};

// ISO (UTC) from the API -> value for a datetime-local input, in local time.
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : null;

const firstError = (data) => {
  if (!data) return null;
  if (typeof data === 'string') return data;
  if (data.detail || data.message) return data.detail || data.message;
  const key = Object.keys(data)[0];
  if (!key) return null;
  const val = Array.isArray(data[key]) ? data[key][0] : data[key];
  return key === 'non_field_errors' ? val : `${key.replace(/_/g, ' ')}: ${val}`;
};

/**
 * Offer Management → Bonus Products.
 *
 * Products customers claim with Wallet Bonus points only, and order on their own
 * in a ₹0 free order (no GST, platform or delivery fee, no other products). Every
 * rule set here is checked again by the server at claim time, so a change takes
 * effect on the customer's next claim even if their screen is stale.
 */
export function BonusProducts() {
  const toast = useRef(null);
  const inventoryList = useSelector((state) => state.inventory.inventory) || [];
  const productList = useSelector((state) => state.products?.products) || [];

  const hubOptions = useMemo(
    () =>
      inventoryList.map((h) => ({
        value: Number(h.value ?? h.id),
        name: h.label || h.place || h.name || `Hub #${h.value ?? h.id}`,
      })),
    [inventoryList]
  );

  // The product list is grouped by hub, so one product can appear more than once.
  const productOptions = useMemo(() => {
    const seen = new Map();
    productList.forEach((p) => {
      const id = Number(p.value ?? p.id);
      if (id && !seen.has(id)) seen.set(id, { id, name: p.name || p.item_name || `Product #${id}` });
    });
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [productList]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [claimsFor, setClaimsFor] = useState(null);
  const [claims, setClaims] = useState([]);
  const [claimsLoading, setClaimsLoading] = useState(false);
  // Wallet Bonus Rewards banner on the app's home page. null until loaded.
  const [showBanner, setShowBanner] = useState(null);
  const [bannerSaving, setBannerSaving] = useState(false);

  const showToast = (severity, summary, detail) => {
    toast.current?.show({ severity, summary, detail, life: 3500 });
  };

  const fetchRows = async () => {
    try {
      setLoading(true);
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;
      const res = await CustomAxios.get('bonus-products/', { params });
      setRows(Array.isArray(res.data) ? res.data : res.data?.results || []);
    } catch (err) {
      showToast('error', 'Error', firstError(err?.response?.data) || 'Failed to load bonus products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetchRows, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter]);

  const hubsLabel = (row) => {
    const places = row.inventory_places || [];
    if (!places.length) return 'All hubs';
    return places.length <= 3 ? places.join(', ') : `${places.slice(0, 3).join(', ')} (+${places.length - 3})`;
  };

  const periodLabel = (row) => {
    const from = formatDate(row.valid_from);
    const until = formatDate(row.valid_until);
    if (!from && !until) return 'Always';
    if (!from) return `Until ${until}`;
    if (!until) return `From ${from}`;
    return `${from} – ${until}`;
  };

  const openCreate = () => {
    setEditRow(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditRow(row);
    setForm({
      product: row.product ? { id: Number(row.product), name: row.product_name || `Product #${row.product}` } : null,
      title: row.title || '',
      points_required: String(row.points_required ?? ''),
      use_within_days: String(row.use_within_days ?? '7'),
      total_quantity: row.total_quantity != null ? String(row.total_quantity) : '',
      per_user_limit: row.per_user_limit != null ? String(row.per_user_limit) : '',
      eligibility: row.eligibility || 'default',
      inventory_ids: (row.inventory_ids || []).map(Number),
      valid_from: toLocalInput(row.valid_from),
      valid_until: toLocalInput(row.valid_until),
      is_active: Boolean(row.is_active),
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.product) {
      showToast('warn', 'Validation', 'Choose a product');
      return;
    }
    if (!(Number(form.points_required) > 0)) {
      showToast('warn', 'Validation', 'Bonus points must be greater than 0');
      return;
    }
    if (!(Number(form.use_within_days) >= 1)) {
      showToast('warn', 'Validation', 'Use within must be at least 1 day');
      return;
    }
    if (form.per_user_limit !== '' && !(Number(form.per_user_limit) >= 1)) {
      showToast('warn', 'Validation', 'Per-customer limit must be at least 1, or blank for unlimited');
      return;
    }
    if (form.valid_from && form.valid_until && new Date(form.valid_until) <= new Date(form.valid_from)) {
      showToast('warn', 'Validation', 'Valid until must be after valid from');
      return;
    }

    const payload = {
      product: Number(form.product.id),
      title: form.title.trim(),
      points_required: Number(form.points_required),
      use_within_days: Number(form.use_within_days),
      total_quantity: form.total_quantity === '' ? null : Number(form.total_quantity),
      per_user_limit: form.per_user_limit === '' ? null : Number(form.per_user_limit),
      eligibility: form.eligibility || 'default',
      inventory_ids: (form.inventory_ids || []).map(Number),
      valid_from: form.valid_from ? new Date(form.valid_from).toISOString() : null,
      valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : null,
      is_active: Boolean(form.is_active),
    };

    try {
      setSaving(true);
      if (editRow) {
        await CustomAxios.patch(`bonus-products/${editRow.id}/`, payload);
        showToast('success', 'Saved', 'Bonus product updated');
      } else {
        await CustomAxios.post('bonus-products/', payload);
        showToast('success', 'Created', 'Bonus product created');
      }
      setModalOpen(false);
      fetchRows();
    } catch (err) {
      showToast('error', 'Error', firstError(err?.response?.data) || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    CustomAxios.get('bonus-rewards-settings/')
      .then((res) => setShowBanner(Boolean(res.data?.show_home_banner)))
      .catch(() => setShowBanner(null));
  }, []);

  const toggleBanner = async (next) => {
    const prev = showBanner;
    setShowBanner(next);
    setBannerSaving(true);
    try {
      const res = await CustomAxios.patch('bonus-rewards-settings/', { show_home_banner: next });
      setShowBanner(Boolean(res.data?.show_home_banner));
      showToast('success', 'Saved', next ? 'Banner is now shown on the app home page' : 'Banner is now hidden from the app home page');
    } catch (err) {
      setShowBanner(prev);
      showToast('error', 'Error', firstError(err?.response?.data) || 'Update failed');
    } finally {
      setBannerSaving(false);
    }
  };

  const toggleActive = async (row) => {
    try {
      await CustomAxios.patch(`bonus-products/${row.id}/`, { is_active: !row.is_active });
      fetchRows();
    } catch (err) {
      showToast('error', 'Error', firstError(err?.response?.data) || 'Update failed');
    }
  };

  const openClaims = async (row) => {
    setClaimsFor(row);
    setClaims([]);
    try {
      setClaimsLoading(true);
      const res = await CustomAxios.get(`bonus-products/${row.id}/claims/`);
      setClaims(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      showToast('error', 'Error', firstError(err?.response?.data) || 'Failed to load claims');
    } finally {
      setClaimsLoading(false);
    }
  };

  const summary = (() => {
    const pts = form.points_required || '—';
    const who = (ELIGIBILITY_OPTIONS.find((o) => o.value === form.eligibility)?.label || 'All customers').toLowerCase();
    const hubs = form.inventory_ids.length
      ? hubOptions.filter((h) => form.inventory_ids.includes(h.value)).map((h) => h.name).join(', ')
      : 'every hub that sells it';
    const per = form.per_user_limit === '' ? 'any number of times' : `${form.per_user_limit} time${form.per_user_limit === '1' ? '' : 's'} each`;
    const qty = form.total_quantity === '' ? 'with no total cap' : `until ${form.total_quantity} are claimed`;
    return `In the app: ${who} at ${hubs} can claim ${form.title || form.product?.name || 'this product'} for ${pts} bonus points, ${per}, ${qty}. Claims not ordered within ${form.use_within_days || 7} days return their points.`;
  })();

  return (
    <Box>
      <Toast ref={toast} />
      <Paper
        variant="outlined"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, p: 2, mb: 2, borderRadius: 2 }}
      >
        <Box>
          <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>Wallet Bonus Rewards banner</Typography>
          <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
            {showBanner === null
              ? 'Could not load this setting.'
              : showBanner
                ? 'Shown on the app home page to signed-in customers whose hub has a bonus product available.'
                : 'Hidden from the app home page. Customers can still open Wallet Bonus from their profile.'}
          </Typography>
        </Box>
        <Switch
          checked={Boolean(showBanner)}
          disabled={showBanner === null || bannerSaving}
          onChange={(e) => toggleBanner(e.target.checked)}
          inputProps={{ 'aria-label': 'Show Wallet Bonus Rewards banner on the app home page' }}
        />
      </Paper>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '13px', color: 'text.secondary', maxWidth: 680 }}>
          Free products customers claim with Wallet Bonus points only. A claimed product is delivered in a separate ₹0
          order: no GST, platform fee or delivery fee, and no other products in it.
        </Typography>
        <GrowthPrimaryButton startIcon={<AddIcon />} onClick={openCreate}>
          Add Bonus Product
        </GrowthPrimaryButton>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label="Search"
          placeholder="Product or title"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {Object.entries(STATUS_CHIPS).map(([value, s]) => (
              <MenuItem key={value} value={value}>{s.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading ? <GrowthPageLoader label="Loading bonus products…" /> : null}
      {!loading && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell>Bonus points</TableCell>
                <TableCell>Claimed / stock</TableCell>
                <TableCell>Per customer</TableCell>
                <TableCell>Eligible</TableCell>
                <TableCell>Valid period</TableCell>
                <TableCell>Hubs</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Available</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const chip = STATUS_CHIPS[row.status] || STATUS_CHIPS.inactive;
                const pct = row.total_quantity ? Math.min(100, (row.claimed_count / row.total_quantity) * 100) : 0;
                return (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                        {row.product_image ? (
                          <Box component="img" src={row.product_image} alt="" sx={{ width: 36, height: 36, borderRadius: 1, objectFit: 'cover' }} />
                        ) : null}
                        <Box>
                          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{row.display_name}</Typography>
                          {row.title ? (
                            <Typography variant="caption" color="text.secondary">{row.product_name}</Typography>
                          ) : null}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{row.points_required}</TableCell>
                    <TableCell sx={{ minWidth: 130 }}>
                      {row.claimed_count} / {row.total_quantity != null ? row.total_quantity : 'Unlimited'}
                      {row.total_quantity != null ? (
                        <LinearProgress variant="determinate" value={pct} sx={{ mt: 0.5, height: 6, borderRadius: 3 }} />
                      ) : null}
                    </TableCell>
                    <TableCell>{row.per_user_limit != null ? row.per_user_limit : 'Unlimited'}</TableCell>
                    <TableCell>{ELIGIBILITY_OPTIONS.find((o) => o.value === row.eligibility)?.label || 'All customers'}</TableCell>
                    <TableCell>{periodLabel(row)}</TableCell>
                    <TableCell>{hubsLabel(row)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={chip.label} sx={{ bgcolor: chip.bg, color: chip.fg, fontWeight: 600 }} />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={Boolean(row.is_active)}
                        onChange={() => toggleActive(row)}
                        inputProps={{ 'aria-label': `Available: ${row.display_name}` }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Edit">
                        <IconButton aria-label="Edit bonus product" onClick={() => openEdit(row)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View claims">
                        <IconButton aria-label="View claims" onClick={() => openClaims(row)}>
                          <ListAltIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    No bonus products yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <GrowthModal
        open={modalOpen}
        onClose={() => !saving && setModalOpen(false)}
        title={editRow ? 'Edit Bonus Product' : 'New Bonus Product'}
        subtitle="A free product customers claim with Wallet Bonus points only."
        busy={saving}
        busyLabel={editRow ? 'Updating…' : 'Creating…'}
        actions={
          <>
            <GrowthSecondaryButton onClick={() => setModalOpen(false)} disabled={saving}>
              Cancel
            </GrowthSecondaryButton>
            <GrowthPrimaryButton onClick={handleSave} loading={saving}>
              {editRow ? 'Save' : 'Create bonus product'}
            </GrowthPrimaryButton>
          </>
        }
      >
        <GrowthFormGrid>
          <GrowthFormSection title="Product & points" />
          <Grid item xs={12}>
            <Autocomplete
              options={productOptions}
              getOptionLabel={(o) => o?.name || ''}
              value={form.product}
              onChange={(_, value) => setForm({ ...form, product: value })}
              isOptionEqualToValue={(o, v) => Number(o.id) === Number(v.id)}
              disabled={Boolean(editRow && editRow.claimed_count > 0)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Product *"
                  helperText={
                    editRow && editRow.claimed_count > 0
                      ? 'Already claimed, so the product cannot change. Add a new bonus product instead.'
                      : 'Customers pay nothing for it. It must be stocked at the hubs below.'
                  }
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Display name"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              helperText="Optional. Blank = the product's own name."
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Bonus points required"
              required
              type="number"
              inputProps={{ min: 1 }}
              value={form.points_required}
              onChange={(e) => setForm({ ...form, points_required: e.target.value })}
              helperText="Taken from Wallet Bonus when the customer claims."
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Use within (days)"
              type="number"
              inputProps={{ min: 1 }}
              value={form.use_within_days}
              onChange={(e) => setForm({ ...form, use_within_days: e.target.value })}
              helperText="Not ordered by then? The points go back."
            />
          </Grid>

          <GrowthFormSection title="Limits & eligibility" />
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Total quantity"
              type="number"
              inputProps={{ min: 1 }}
              value={form.total_quantity}
              onChange={(e) => setForm({ ...form, total_quantity: e.target.value })}
              helperText={`Blank = unlimited. Claimed so far: ${editRow?.claimed_count ?? 0}.`}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Per-customer limit"
              type="number"
              inputProps={{ min: 1 }}
              value={form.per_user_limit}
              onChange={(e) => setForm({ ...form, per_user_limit: e.target.value })}
              helperText="Times one customer can claim it. Blank = unlimited."
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              size="small"
              label="Eligible customers"
              value={form.eligibility}
              onChange={(e) => setForm({ ...form, eligibility: e.target.value })}
            >
              {ELIGIBILITY_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={hubOptions}
              getOptionLabel={(option) => option.name}
              value={hubOptions.filter((h) => form.inventory_ids.includes(Number(h.value)))}
              onChange={(_, newValue) => setForm({ ...form, inventory_ids: newValue.map((h) => Number(h.value)) })}
              renderInput={(params) => (
                <TextField {...params} size="small" label="Hubs (optional)" placeholder="Leave empty for all hubs"
                  helperText="Empty = every hub that sells the product." />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip key={option.value} label={option.name} size="small" {...getTagProps({ index })} />
                ))
              }
              isOptionEqualToValue={(option, value) => Number(option.value) === Number(value.value)}
              limitTags={3}
            />
          </Grid>

          <GrowthFormSection title="Validity period" />
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Valid from"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={form.valid_from}
              onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
              helperText="Blank = available now."
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Valid until"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={form.valid_until}
              onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
              helperText="Blank = no end date."
            />
          </Grid>
          <Grid item xs={12}>
            <GrowthFormSwitch
              label="Available"
              helperText="Turn off to hide it from the app and stop new claims right away."
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ bgcolor: '#FDF0F6', color: '#5A2340', borderRadius: 2, p: 1.5, fontSize: 13 }}>{summary}</Box>
          </Grid>
        </GrowthFormGrid>
      </GrowthModal>

      <GrowthModal
        open={Boolean(claimsFor)}
        onClose={() => setClaimsFor(null)}
        title={`Claims · ${claimsFor?.display_name || ''}`}
        subtitle="Most recent first (up to 500)."
        actions={<GrowthSecondaryButton onClick={() => setClaimsFor(null)}>Close</GrowthSecondaryButton>}
      >
        {claimsLoading ? (
          <GrowthPageLoader label="Loading claims…" />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Customer</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>Hub</TableCell>
                  <TableCell>Points</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Order</TableCell>
                  <TableCell>Claimed on</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {claims.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>{c.user_name}</TableCell>
                    <TableCell>{c.mobileno}</TableCell>
                    <TableCell>{c.hub || '—'}</TableCell>
                    <TableCell>{c.points}</TableCell>
                    <TableCell>{CLAIM_STATUS[c.status] || c.status}</TableCell>
                    <TableCell>{c.order_id || '—'}</TableCell>
                    <TableCell>{new Date(c.created_on).toLocaleString('en-IN')}</TableCell>
                  </TableRow>
                ))}
                {claims.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">No claims yet</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </GrowthModal>
    </Box>
  );
}
